import { useForm } from './Form';
import { classNames } from 'libs/react';
import { useEffect } from 'libs/react';
import { useState } from 'libs/react';
import { FormInput } from 'components/form/FormInput';
import { LanguageSelect } from 'components/form/private/LanguageSelect';
import { React } from 'libs/react';
import './TranslatableFormInput.scss';
import { useUser } from 'components/user/UserProvider';
/**
 * @component TranslatableFormInput
 * @since 1.0.0
 */
export function TranslatableFormInput(props) {
    /*
     * Note for future users:
     * This components needs to be wrapped inside a form to work properly
     * otherwise values will not be displayed correctly when selecting
     * a new language.
     */
    var _a;
    let { path, name, type, size, style, badge, picto, label, infos, scale, error, required, disabled, placeholder, autoselect, autocorrect, autocomplete, autocapitalize, languages, tooltip } = Object.assign({ style: 'normal', path: '' }, props);
    const form = useForm();
    const user = useUser();
    const [skipError, setSkipError] = useState(false);
    const [language, setLanguage] = useState(languages.indexOf(languages.filter((lang) => { var _a, _b; return lang.code === ((_b = (_a = user.user) === null || _a === void 0 ? void 0 : _a.language) === null || _b === void 0 ? void 0 : _b.code); })[0]));
    const [values, setValues] = useState(extract(name, (_a = props.defaultValues) !== null && _a !== void 0 ? _a : props.values));
    /**
     * @function getName
     * @since 1.0.0
     * @hidden
     */
    function getName(lang) {
        return (path ? path + '.' : '') + lang + '.' + name;
    }
    /**
     * @function getValue
     * @since 1.0.0
     * @hidden
     */
    function getValue(lang) {
        return values && values[lang] && values[lang][name];
    }
    /**
     * @function getError
     * @since 1.0.0
     * @hidden
     */
    function getError(lang) {
        return form.getError(getName(lang));
    }
    /**
     * @function onBlur
     * @since 1.0.0
     * @hidden
     */
    function onBlur() {
        if (props.onBlur) {
            props.onBlur();
        }
    }
    /**
     * @function onFocus
     * @since 1.0.0
     * @hidden
     */
    function onFocus() {
        if (props.onFocus) {
            props.onFocus();
        }
    }
    /**
     * @function onChange
     * @since 1.0.0
     * @hidden
     */
    function onChange(value) {
        if (values[language] == null) {
            values[language] = {};
        }
        values[language][name] = value;
        // if we have an error
        if (getError(language) != null) {
            setSkipError(true);
        }
        if (props.onChange) {
            props.onChange(languages[language], value);
        }
    }
    /**
     * @function onLanguageChange
     * @since 1.0.0
     * @hidden
     */
    function onLanguageChange(language) {
        setLanguage(language);
    }
    useEffect(() => {
        if (props.defaultValues)
            setValues(extract(name, props.defaultValues));
    }, [props.defaultValues]);
    useEffect(() => {
        if (!props.defaultValues && props.values)
            setValues(extract(name, props.values));
    }, [props.values]);
    useEffect(() => {
        /*
         * If we have an error in a specific language,
         * make sure we show the incorrect value in this
         * specific language.
         */
        for (let i = 0; i < languages.length; i++) {
            const error = getError(i);
            if (error && !skipError) {
                setLanguage(i);
                break;
            }
        }
    });
    const className = classNames('translatable-form-input', 'translatable-form-input--' + style, props.className);
    return (React.createElement("div", { className: className }, languages.map((lang, index) => {
        const value = getValue(index);
        if (error == null) {
            error = getError(index);
        }
        /**
         * Make sure we do not make an hidden field required because
         * the browser will try to show the required error but it will
         * be hidden by the container. Rely on server validation for
         * these types of error.
         */
        const isRequired = required && language == index;
        const className = classNames('translatable-form-input-language', 'translatable-form-input-language--' + style, { 'translatable-form-input-language--inactive': language != index });
        return (React.createElement("div", { key: lang.code, className: className },
            React.createElement(FormInput, { name: getName(index), type: type, size: size, style: style, badge: badge, picto: picto, label: label, infos: infos, value: !props.defaultValues ? value : undefined, defaultValue: props.defaultValues ? value : undefined, error: error, scale: scale, required: isRequired, disabled: disabled, placeholder: placeholder, autoselect: autoselect, autocorrect: autocorrect, autocomplete: autocomplete, autocapitalize: autocapitalize, onBlur: onBlur, onFocus: onFocus, onChange: onChange, prefix: React.createElement(LanguageSelect, { current: languages[language], options: languages, onSelect: (language) => onLanguageChange(languages.indexOf(language)) }), tooltip: tooltip })));
    })));
}
/**
 * @function extract
 * @since 1.0.0
 * @hidden
 */
function extract(name, values) {
    return values && Array.isArray(values)
        ? values.map((value) => ({ [name]: value[name] }))
        : [{ [name]: values }];
}
